'use client';

import { c as _c } from "react/compiler-runtime";
import { type KeyboardEvent, useRef } from 'react';
import { SPARTAN_LINEUP_DATA_GA, SPARTAN_LINEUP_TRACKING } from '@/app/lcv/components/sections/spartan/shared/lineup/tracking';
import type { SpartanLineupGroupKey, SpartanLineupGroups, SpartanLineupTone } from '@/app/lcv/components/sections/spartan/shared/lineup/types';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
interface SpartanLineupGroupTabsProps {
  groups: SpartanLineupGroups;
  value: SpartanLineupGroupKey;
  tone: SpartanLineupTone;
  eventName: string;
  panelId: string;
  tabIdPrefix: string;
  onChange: (value: SpartanLineupGroupKey) => void;
}
const GROUP_ORDER = ['pickup', 'ppv'] as const satisfies readonly SpartanLineupGroupKey[];
export const SpartanLineupGroupTabs = t0 => {
  const $ = _c(29);
  const {
    groups,
    value,
    tone,
    eventName,
    panelId,
    tabIdPrefix,
    onChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const tabRefs = useRef(t1);
  const isDark = tone === "dark";
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[1] !== eventName || $[2] !== groups || $[3] !== isDark || $[4] !== onChange || $[5] !== panelId || $[6] !== tabIdPrefix || $[7] !== value) {
    const selectAt = index => {
      const nextIndex = (index + GROUP_ORDER.length) % GROUP_ORDER.length;
      const nextValue = GROUP_ORDER[nextIndex];
      if (!nextValue) {
        return;
      }
      tabRefs.current[nextIndex]?.focus();
      onChange(nextValue);
    };
    const handleKeyDown = (event, index_0) => {
      bb13: switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          {
            event.preventDefault();
            selectAt(index_0 + 1);
            break bb13;
          }
        case "ArrowLeft":
        case "ArrowUp":
          {
            event.preventDefault();
            selectAt(index_0 - 1);
            break bb13;
          }
        case "Home":
          {
            event.preventDefault();
            selectAt(0);
            break bb13;
          }
        case "End":
          {
            event.preventDefault();
            selectAt(GROUP_ORDER.length - 1);
            break bb13;
          }
        default:
      }
    };
    t2 = "tablist";
    t3 = "\u0E1B\u0E23\u0E30\u0E40\u0E20\u0E17\u0E23\u0E16";
    const t7 = isDark ? "border-gray-500" : "border-[#C4C4C4]";
    if ($[13] !== t7) {
      t4 = cn("xs:max-w-94 relative mx-auto mt-6 grid h-12 w-full grid-cols-2 border-b-2 md:h-14 lg:mx-0 lg:mt-0 lg:w-94", t7);
      $[13] = t7;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = createDataAttributes({
        test: SPARTAN_LINEUP_TRACKING.groupTabs
      });
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t6 = GROUP_ORDER.map((groupKey, index_1) => {
      const isSelected = value === groupKey;
      const tabId = `${tabIdPrefix}-${groupKey}`;
      return <button key={groupKey} ref={node => {
        tabRefs.current[index_1] = node;
      }} id={tabId} type="button" role="tab" aria-selected={isSelected} aria-controls={panelId} tabIndex={isSelected ? 0 : -1} className={cn("text-body-2 font-noto-bold relative z-10 flex h-full w-full cursor-pointer items-center justify-center px-2 whitespace-nowrap text-gray-400 transition-colors duration-300 ease-out outline-none motion-reduce:transition-none", isSelected && (isDark ? "text-white" : "text-gray-900"), !isSelected && (isDark ? "hover:text-white focus-visible:text-white" : "hover:text-gray-800 focus-visible:text-gray-800"))} onClick={() => onChange(groupKey)} onKeyDown={event_0 => handleKeyDown(event_0, index_1)} {...createDataAttributes({
        test: SPARTAN_LINEUP_TRACKING.groupTab(index_1),
        selected: isSelected
      })} {...createDataGA({
        eventName,
        group: SPARTAN_LINEUP_DATA_GA.section,
        click: true,
        text: groups[groupKey].label
      })}>{groups[groupKey].label}</button>;
    });
    $[1] = eventName;
    $[2] = groups;
    $[3] = isDark;
    $[4] = onChange;
    $[5] = panelId;
    $[6] = tabIdPrefix;
    $[7] = value;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
  }
  const t7 = isDark ? "bg-white" : "bg-gray-800";
  const t8 = value === "ppv" && "translate-x-full";
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = cn("pointer-events-none absolute -bottom-0.5 left-0 z-0 h-0.5 w-1/2 transition-transform duration-300 ease-out motion-reduce:duration-0", t7, t8);
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = createDataAttributes({
      test: SPARTAN_LINEUP_TRACKING.groupIndicator
    });
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t9) {
    t11 = <span aria-hidden="true" className={t9} {...t10} />;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t2 || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6) {
    t12 = <div role={t2} aria-label={t3} className={t4} {...t5}>{t6}{t11}</div>;
    $[22] = t11;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
};